import { defineStore } from 'pinia';
import { useSystemStore } from '@/lib/stores/SystemStore';
import ShiftWindowApi from '../data/ShiftWindowApi';
import {
    CrewForWeek,
    CycleType, EquipmentUsageInDepartmentSummaryViewModel,
    ShiftCommunication,
    ShiftLocationsViewModel,
    ShiftLocationRefreshQueryResponse,
    ShiftPlannedTask,
    StaffRole,
    SupervisorReportViewModel,
    TimeUnit
} from '@/models/api';
import ClientTaskModel from '@/models/client/client-task';
import { transformRowsToHeadings, transformTaskToClientTask } from './Transforms';
import { useDepartmentStore } from './DepartmentStore';
import { useShiftCommunication, useShiftCrew, useShiftDetails, useShiftEquipment } from './Shift';
import { useHeadingsStore } from './HeadingsStore';
import Equipment from '@/models/client/equipment';
import { transformEquipment, transformPlannedEquipment } from '@/models/transforms/EquipmentTransforms';
import ClientRowModel from '@/models/client/client-row';
import dayjs from 'dayjs';
import { useMineAreaStore } from './MineAreaStore';
import { Shift } from '@/models/api/Queries/shift';
import { ShiftCrewMember } from '../services/Staff/type';
import ProductionFronts from '@/lib/data/ProductionFronts';
import { useShiftWindowActuals } from '@/lib/stores/Production/ShiftWindowActualsStore';
import ShiftApi from '@/lib/data/Shift';
import Logging from '@/lib/Logging';
import { EventBus, Events } from '@/lib/EventBus';
import { useShiftHighLevelActuals } from '@/lib/stores/Shift/ShiftHighLevelActualsStore';
import { useSyncQueueStore } from '@/lib/stores/SyncQueueStore';
import task from '@/components/Board/Task.vue';
import _, { DebouncedFunc } from 'lodash';

// Per-location refresh debounce: coalesce notification bursts into one refresh, trailing-only.
const LOCATION_REFRESH_DEBOUNCE_MS = 5000;
const pendingLocationRefreshes = new Map<string, DebouncedFunc<() => void>>();

// Per-location generation guard: discard a stale in-flight refresh if a newer one has started.
const locationRefreshGenerations = new Map<string, number>();

export interface ShiftDetails {
    shiftId: string;
    weekId: string;
    timeUnits: TimeUnit[];
    hasShiftPlan: boolean;
    hasWeekPlan: boolean;
    hasActuals: boolean;
    deploymentMeetingId: string | null;
    handoverMeetingId: string | null;
    weekDate: string;
    shiftDate: string;
    shiftNumber: number;
    locationsForShift: ShiftLocationsViewModel | null;
    nextShiftDetails: NextShiftDetails[];
    shiftName: string;
}

export interface NextShiftDetails {
    timeUnits: TimeUnit[];
    weekDate: string;
    shiftName: string;
}

interface ClientShift {
    plannedEquipment: Equipment[];
    communication: ShiftCommunication;
    headings: ClientRowModel[];
    shiftDetails: ShiftDetails;
    supervisorReport: SupervisorReportViewModel | null;
    shiftCrewMembers: ShiftCrewMember[];
    shiftRoles: StaffRole[];
    shiftCrew: any;
    assignedCrewIds: string[];
    weekCrewTemplates: CrewForWeek[];
}

interface EmptyShift {
    shiftDetails: { hasWeekPlan: boolean; hasShiftPlan: boolean };
}

export const useShiftWindowStore = defineStore('shiftWindow', {
    state: () => {
        return {
            isInitialized: false,
            _activeShiftIndex: 0,
            windowBase: null as { mineId: string; departmentId: string; date: string; shiftNumber: string } | null,
            shifts: [] as (ClientShift | EmptyShift)[]
        };
    },
    getters: {
        hasPreviousShift(): boolean {
            return this._activeShiftIndex > 0;
        },
        hasNextShift(): boolean {
            return this._activeShiftIndex < this.shifts.length - 1;
        },
        isEmptyShift(): boolean {
            return (
                this.activeShift.shiftDetails.hasShiftPlan === false ||
                this.activeShift.shiftDetails.hasWeekPlan === false
            );
        },
        activeShift(): ClientShift | EmptyShift {
            return this.shifts[this._activeShiftIndex];
        },
        windowShifts(): { name: string; date: string }[] {
            const shifts = this.shifts.map((shift) => {
                // @ts-ignore
                return { name: shift.shiftName!, date: shift.shiftDetails.timeUnits[0].startTime.substring(0, 10) };
            });
            return shifts;
        },
    },
    actions: {
        async GetOtherDepartmentEquipmentUsage(departmentId: string, date: string, shiftNumber: string): Promise<EquipmentUsageInDepartmentSummaryViewModel[]> {
              try {
                  return (await ShiftApi.getEquipmentUsageForShiftWindow(departmentId, date, shiftNumber, 1, 2)).equipmentUsages;
              } catch(e:any){
                  Logging.error(e);
                  // make sure that no matter what we don't fail the whole thing if we can't retrieve the equipment usage
                  EventBus.$emit(Events.ToastError, 'Failed to retrieve equipment usage for other departments.');
                  return [];
              }
        },
        async refreshOtherDepartmentEquipmentUsage() {
            if (!useSystemStore().isOnline) return;
            if (!this.windowBase) return;

            const { departmentId, date, shiftNumber } = this.windowBase;
            const usage = await this.GetOtherDepartmentEquipmentUsage(departmentId, date, shiftNumber);
            useHeadingsStore().updateOtherDepartmentEquipmentUsage(usage);
        },
        async populateWindow(mineId: string, departmentId: string, date: string, shiftNumber: string) {
            this.isInitialized = false;
            this.windowBase = { mineId, departmentId, date, shiftNumber };

            let tasks: ClientTaskModel[] = [];

            if (useSystemStore().isOnline) {
                const current = await ShiftWindowApi.getShiftWindowOffset(mineId, departmentId, date, shiftNumber, 0);

                const hasRingSupportingProductionLocations = current.shift?.headings?.some(x=>x.location?.stopeInfo?.supportsRings == true) ?? true;

                if(hasRingSupportingProductionLocations)
                    await useShiftWindowActuals().populateWindow(mineId, departmentId, date, shiftNumber, 1, 2);

                useShiftHighLevelActuals().populateWindow(mineId, departmentId, date, shiftNumber, 1, 2);

                const otherDepartmentEquipmentAvailability = await this.GetOtherDepartmentEquipmentUsage(departmentId, date, shiftNumber);

                tasks = current.tasks.map((x: ShiftPlannedTask) =>
                    transformTaskToClientTask(x, useDepartmentStore().taskTypes, useDepartmentStore().locations, useShiftWindowActuals().getBlastPacketTargetsDisplayInformation)
                );

                const transformShiftToClientShift = (x: Shift): ClientShift | EmptyShift => {
                    if (x.hasWeekPlan === false) {
                        return { shiftDetails: { hasWeekPlan: x.hasWeekPlan, hasShiftPlan: x.hasShiftPlan } };
                    }

                    if (x.hasShiftPlan === false) {
                        return { shiftDetails: { hasWeekPlan: x.hasWeekPlan, hasShiftPlan: x.hasShiftPlan } };
                    }

                    const shiftCrew = {
                        _type: 'CrewForShift',
                        ...x.shiftCrew,
                        shiftCrewMembers:
                            x.shiftCrew?.shiftCrewMembers
                                .filter((crewMember) => crewMember.staffRoleId && crewMember.staffId)
                                .map((crewMember) => {
                                    return {
                                        ...crewMember,
                                        staffId: crewMember.staffId!,
                                        staffRoleId: crewMember.staffRoleId!,
                                        staff: useSystemStore().getStaffById(crewMember.staffId!),
                                        staffRole: useSystemStore().getStaffRoleById(crewMember.staffRoleId!),
                                    };
                                }) ?? [],
                    };

                    const shiftRoles =
                        x.shiftCrew?.shiftCrewMembers.reduce((roleList: StaffRole[], crewMember: any) => {
                            const roleIndex = roleList.findIndex((role) => role.id === crewMember.staffRoleId);

                            if (roleIndex === -1) {
                                const role = useSystemStore().getStaffRoleById(crewMember.staffRoleId!);
                                roleList.push(role);
                            }

                            return roleList;
                        }, []) ?? [];

                    return {
                        shiftDetails: {
                            nextShiftDetails: x.nextShifts.map(ns=>(
                                {
                                timeUnits: ns.timeUnits,
                                    // @ts-ignore
                                weekDate: ns.weekOf.substring(0,10),
                                shiftName: ns.shiftName ?? '?',
                                }
                            )),
                            shiftId: x.shiftId!,
                            weekId: x.weekId!,
                            timeUnits: x.timeUnits,
                            hasShiftPlan: x.hasShiftPlan,
                            hasWeekPlan: x.hasWeekPlan,
                            hasActuals: x.hasActuals,
                            handoverMeetingId: x.handoverMeetingId ?? null,
                            deploymentMeetingId: x.deploymentMeetingId ?? null,
                            // @ts-ignore
                            weekDate: x.weekOf.substring(0, 10),
                            // @ts-ignore
                            shiftDate: x.shiftDate,
                            shiftNumber: x.shiftNumber,
                            locationsForShift: x.locations!,
                            shiftName: x.shiftName ?? '?',
                        },
                        plannedEquipment: transformEquipment(
                            x.plannedEquipment,
                            x.shiftId!,
                            useSystemStore().equipmentRoles
                        ),
                        communication: x.communication!,
                        headings: transformRowsToHeadings(
                            x.headings,
                            tasks,
                            dayjs.utc(x.timeUnits[0].startTime),
                            dayjs.utc(x.timeUnits[x.timeUnits.length - 1].endTime),
                            dayjs.utc(x.shiftDate),
                            x.shiftNumber,
                            useMineAreaStore().activeShiftSchedule,
                            x.nextShifts.map(ns=>dayjs.utc(ns.timeUnits[0].startTime)),
                            x.nextShifts.map(ns=>dayjs.utc(ns.timeUnits[ns.timeUnits.length - 1].endTime))
                        ),
                        supervisorReport: x.supervisorReport,
                        // @ts-ignore
                        shiftCrewMembers: shiftCrew.shiftCrewMembers,
                        shiftCrew: shiftCrew,
                        shiftRoles: shiftRoles,
                        assignedCrewIds: x.assignedCrewIds,
                        weekCrewTemplates: x.weekCrewTemplates,
                    };
                };

                this.shifts = [
                    {} as EmptyShift,
                    transformShiftToClientShift(current.shift as Shift),
                    {} as EmptyShift,
                    {} as EmptyShift,
                ];

                const loadShiftWindow = () => {
                    // load the rest of the shifts
                    ShiftWindowApi.getShiftWindowOffset(
                        mineId,
                        departmentId,
                        date,
                        shiftNumber,
                        -1
                    ).then((result)=>{
                        const previousShift = result.shift;

                        return ShiftWindowApi.getShiftWindowOffset(mineId, departmentId, date, shiftNumber, 1)
                            .then(result=>({
                                previousShift: previousShift,
                                nextShift: result.shift
                            }));
                    }).then(({previousShift,nextShift})=>{
                        return ShiftWindowApi.getShiftWindowOffset(mineId, departmentId, date, shiftNumber, 2)
                            .then(result=>({
                                previousShift: previousShift,
                                nextShift: nextShift,
                                shiftAfterNext: result.shift
                            }));
                    }).then(({previousShift,nextShift, shiftAfterNext})=>{
                        this.shifts[0] = transformShiftToClientShift(previousShift as Shift);
                        this.shifts[2] = transformShiftToClientShift(nextShift as Shift);
                        this.shifts[3] = transformShiftToClientShift(shiftAfterNext as Shift);

                        if(!hasRingSupportingProductionLocations){
                            const allShifts = [previousShift, nextShift, shiftAfterNext];

                            if(allShifts.some(s=>s?.headings?.some(x=>x.location?.stopeInfo?.supportsRings == true)))
                                useShiftWindowActuals().populateWindow(mineId, departmentId, date, shiftNumber, 1, 2);
                        }
                    });
                };

                this._activeShiftIndex = 1;

                if (this.isEmptyShift) {
                    useShiftDetails().setupEmptyShift(
                        this.activeShift.shiftDetails.hasWeekPlan,
                        this.activeShift.shiftDetails.hasShiftPlan
                    );
                    loadShiftWindow();

                    return;
                }

                // GET RID OF
                const productionFrontRulesets = hasRingSupportingProductionLocations ? (await ProductionFronts.listRulesets(departmentId)).productionFronts : [];

                await useShiftDetails().setUpStore(
                    (this.activeShift as ClientShift).shiftDetails,
                    (this.activeShift as ClientShift).supervisorReport
                );

                useHeadingsStore().setUpStore((this.activeShift as ClientShift).headings, productionFrontRulesets, otherDepartmentEquipmentAvailability);

                ShiftApi.cutsAndChainage(current.shift!.shiftId!).then(result=>{
                    useHeadingsStore().addTaskGroupCutInformation(result.plannedTaskGroupCutAndChainages);
                });

                loadShiftWindow();
            } else {
                if (this.isEmptyShift) {
                    useShiftDetails().setupEmptyShift(
                        this.activeShift.shiftDetails.hasWeekPlan,
                        this.activeShift.shiftDetails.hasShiftPlan
                    );

                    return;
                }

                await useShiftDetails().setUpStore(
                    (this.activeShift as ClientShift).shiftDetails,
                    (this.activeShift as ClientShift).supervisorReport
                );

                useHeadingsStore().updateStore((this.activeShift as ClientShift).headings);
            }

            useShiftCommunication().setUpStore((this.activeShift as ClientShift).communication);
            useShiftEquipment().setUpStore((this.activeShift as ClientShift).plannedEquipment,
                // @ts-ignore
                (this.activeShift as ClientShift).communication.equipmentInWorkshop.map((x) => x.equipmentId),
                //@ts-ignore
                (this.activeShift as ClientShift).communication.equipmentToWorkshop.map((x) => x.equipmentId));

            useShiftCrew().setUpStore(
                (this.activeShift as ClientShift).shiftCrewMembers,
                (this.activeShift as ClientShift).shiftRoles,
                // @ts-ignore
                this.activeShift.assignedCrewIds,
                // @ts-ignore
                this.activeShift.weekCrewTemplates,
                (this.activeShift as ClientShift).shiftCrew?.id
            );

            this.isInitialized = true;
        },
        goToPreviousShift() {
            this._activeShiftIndex -= 1;
        },
        goToNextShift() {
            this._activeShiftIndex += 1;
        },
        async refreshEquipment(equipmentId: string) {
            if (!useSystemStore().isOnline) return;
            if (!this.windowBase) return;
            if (this.isEmptyShift) return;

            const { mineId, departmentId, date, shiftNumber } = this.windowBase;
            const offset = this._activeShiftIndex - 1;

            let response;
            try {
                response = await ShiftWindowApi.getShiftEquipmentRefresh(
                    mineId,
                    departmentId,
                    date,
                    shiftNumber,
                    equipmentId,
                    offset
                );
            } catch (e: any) {
                Logging.error(e);
                return;
            }

            if (!response?.plannedEquipment) return;

            const shiftId = (this.activeShift as ClientShift).shiftDetails.shiftId;
            const [equipment] = transformPlannedEquipment(
                [response.plannedEquipment],
                shiftId,
                useSystemStore().equipmentRoles
            );
            if (!equipment) return;

            useShiftEquipment().updatePlannedEquipment(equipment);
            useHeadingsStore().patchTasksForEquipment(equipment);
        },
        async refreshAllEquipment() {
            if (!useSystemStore().isOnline) return;
            if (!this.windowBase) return;
            if (this.isEmptyShift) return;

            const { mineId, departmentId, date, shiftNumber } = this.windowBase;
            const offset = this._activeShiftIndex - 1;

            let response;
            try {
                response = await ShiftWindowApi.getAllShiftEquipmentRefresh(
                    mineId,
                    departmentId,
                    date,
                    shiftNumber,
                    offset
                );
            } catch (e: any) {
                Logging.error(e);
                return;
            }

            if (!response?.plannedEquipment) return;

            const shiftId = (this.activeShift as ClientShift).shiftDetails.shiftId;
            const equipment = transformPlannedEquipment(
                response.plannedEquipment,
                shiftId,
                useSystemStore().equipmentRoles
            );

            useShiftEquipment().updateAllPlannedEquipment(equipment);
            useHeadingsStore().patchTasksForEquipmentList(equipment);
        },
        refreshLocation(locationId: string) {
            let debounced = pendingLocationRefreshes.get(locationId);
            if (!debounced) {
                debounced = _.debounce(() => {
                    this._performLocationRefresh(locationId);
                }, LOCATION_REFRESH_DEBOUNCE_MS);
                pendingLocationRefreshes.set(locationId, debounced);
            }
            debounced();
        },
        async _performLocationRefresh(locationId: string) {
            if (!useSystemStore().isOnline) return;
            if (!this.windowBase) return;

            const headingsStore = useHeadingsStore();

            // Only refresh if this location is currently displayed on the active board.
            if (!headingsStore.hasHeadingForLocation(locationId)) return;

            // Stamp this refresh; a newer one starting makes our results stale.
            const myGeneration = (locationRefreshGenerations.get(locationId) ?? 0) + 1;
            locationRefreshGenerations.set(locationId, myGeneration);
            const isStale = () => locationRefreshGenerations.get(locationId) !== myGeneration;

            const { mineId, departmentId, date, shiftNumber } = this.windowBase;

            // Actuals/ring-states are cross-location (a change in one location can affect others), so we
            // can't refresh them per-location — re-pull the entire actuals window. Gate on the cached
            // window containing ring-supporting production locations, mirroring populateWindow.
            const windowHasRingSupportingLocations = this.shifts.some(
                (shift) => (shift as ClientShift)?.headings?.some((row) => row.stopeInfo?.supportsRings == true)
            );
            const actualsRefresh = windowHasRingSupportingLocations
                ? useShiftWindowActuals().populateWindow(mineId, departmentId, date, shiftNumber, 1, 2)
                : Promise.resolve();

            // Fetch the location's latest heading + tasks for a single populated shift in the window,
            // anchored on the window base (shifts[1]). Window index i corresponds to offset i - 1.
            const fetchShiftRefresh = async (
                index: number
            ): Promise<{ index: number; clientHeading: ClientRowModel } | null> => {
                const clientShift = this.shifts[index] as ClientShift;
                if (!clientShift?.shiftDetails?.timeUnits?.length) return null;

                // Skip shifts that don't currently show this location.
                if (!clientShift.headings?.some((row) => row.location?.id === locationId)) return null;

                let response;
                try {
                    response = await ShiftWindowApi.getShiftLocationRefresh(
                        mineId,
                        departmentId,
                        date,
                        shiftNumber,
                        locationId,
                        index - 1
                    );
                } catch (e: any) {
                    Logging.error(e);
                    return null;
                }

                if (!response?.heading) return null;

                const clientHeading = this.transformLocationHeading(response, clientShift.shiftDetails);
                if (!clientHeading) return null;

                return { index, clientHeading };
            };

            // The user's local unsynced edits to this location can no longer be applied — discard and
            // notify once, on the first successful application (regardless of which shift it came from).
            let pendingSyncHandled = false;
            const applyResult = ({ index, clientHeading }: { index: number; clientHeading: ClientRowModel }) => {
                // Discard now-stale results if a newer refresh has started.
                if (isStale()) return;

                if (!pendingSyncHandled) {
                    pendingSyncHandled = true;
                    const hadPendingSync = headingsStore.discardPendingSyncForLocation(locationId);
                    if (hadPendingSync) {
                        EventBus.$emit(
                            Events.ToastError,
                            'Your unsynced changes to this location were replaced by an update from another device.'
                        );
                    }
                }

                if (index === this._activeShiftIndex) {
                    // Active shift: route through the HeadingsStore for validation + shine highlight.
                    headingsStore.replaceHeadingForLocation(clientHeading);
                }

                // Always patch the cached window shift so offline navigation stays correct.
                this.patchShiftHeading(this.shifts[index] as ClientShift, clientHeading);
            };

            // Re-pull the full actuals window in parallel with the active shift's heading refresh, and
            // apply the currently viewed shift first so it updates as soon as possible. Wait for both
            // before applying, so the refresh reflects the freshest actuals/ring states.
            const activeIndex = this._activeShiftIndex;
            const [activeResult] = await Promise.all([fetchShiftRefresh(activeIndex), actualsRefresh]);
            if (isStale()) return;
            if (activeResult) applyResult(activeResult);
            if(!this.isEmptyShift)
                ShiftApi.cutsAndChainage((this.activeShift as ClientShift).shiftDetails.shiftId).then(result=>{
                    if(isStale()) return;
                    useHeadingsStore().addTaskGroupCutInformation(result.plannedTaskGroupCutAndChainages);
                });

            // Then refresh the remaining shifts in parallel.
            const remainingResults = await Promise.all(
                this.shifts.map((shift, index) => index).filter((index) => index !== activeIndex).map(fetchShiftRefresh)
            );
            if (isStale()) return;

            for (const result of remainingResults) {
                if (result) applyResult(result);
            }
        },
        transformLocationHeading(
            response: ShiftLocationRefreshQueryResponse,
            shiftDetails: ShiftDetails
        ): ClientRowModel | undefined {
            if (!response.heading) return undefined;

            const tasks = response.tasks.map((x) =>
                transformTaskToClientTask(
                    x,
                    useDepartmentStore().taskTypes,
                    useDepartmentStore().locations,
                    useShiftWindowActuals().getBlastPacketTargetsDisplayInformation
                )
            );

            const nextShifts = shiftDetails.nextShiftDetails ?? [];

            const [clientHeading] = transformRowsToHeadings(
                [response.heading],
                tasks,
                dayjs.utc(shiftDetails.timeUnits[0].startTime),
                dayjs.utc(shiftDetails.timeUnits[shiftDetails.timeUnits.length - 1].endTime),
                dayjs.utc(shiftDetails.shiftDate),
                shiftDetails.shiftNumber,
                useMineAreaStore().activeShiftSchedule,
                nextShifts.map((ns) => dayjs.utc(ns.timeUnits[0].startTime)),
                nextShifts.map((ns) => dayjs.utc(ns.timeUnits[ns.timeUnits.length - 1].endTime))
            );

            return clientHeading;
        },
        patchShiftHeading(shift: ClientShift, clientHeading: ClientRowModel) {
            const index = shift.headings.findIndex((row) => row.location?.id === clientHeading.location?.id);
            if (index === -1) return;

            shift.headings.splice(index, 1, clientHeading);
        },
        removeLocations(locationIds: string[]) {
            if (!locationIds.length) return;

            const idSet = new Set(locationIds);

            // Strip the locations from every cached shift in the window so navigation stays consistent.
            for (const shift of this.shifts) {
                const clientShift = shift as ClientShift;
                if (!clientShift?.headings?.length) continue;

                const remaining = clientShift.headings.filter((row) => !row.location || !idSet.has(row.location.id));
                if (remaining.length !== clientShift.headings.length) {
                    clientShift.headings.splice(0, clientShift.headings.length, ...remaining);
                }
            }

            // Update the active shift's live display + validation.
            useHeadingsStore().removeHeadingsForLocations(locationIds);
        },
        async addLocations(planId: string, locationIds: string[]) {
            if (!locationIds.length) return;
            if (!useSystemStore().isOnline) return;
            if (!this.windowBase) return;

            const headingsStore = useHeadingsStore();
            const { mineId, departmentId, date, shiftNumber } = this.windowBase;

            // Only cached shifts that belong to the target plan are relevant — the window can span
            // adjacent weeks/plans, and a location added to one plan must not touch another's shifts.
            const planShiftIndexes = this.shifts
                .map((shift, index) => ({ clientShift: shift as ClientShift, index }))
                .filter(
                    ({ clientShift }) =>
                        clientShift?.shiftDetails?.weekId === planId && !!clientShift.shiftDetails.timeUnits?.length
                )
                .map(({ index }) => index);

            if (!planShiftIndexes.length) return;

            // Skip locations already present in any shift belonging to this plan.
            const toAdd = locationIds.filter(
                (locationId) =>
                    !planShiftIndexes.some((index) =>
                        (this.shifts[index] as ClientShift).headings?.some((row) => row.location?.id === locationId)
                    )
            );
            if (!toAdd.length) return;

            const fetchAndInsert = async (locationId: string, index: number) => {
                const clientShift = this.shifts[index] as ClientShift;
                if (clientShift.headings?.some((row) => row.location?.id === locationId)) return;

                let response: ShiftLocationRefreshQueryResponse | null;
                try {
                    response = await ShiftWindowApi.getShiftLocationRefresh(
                        mineId,
                        departmentId,
                        date,
                        shiftNumber,
                        locationId,
                        index - 1
                    );
                } catch (e: any) {
                    Logging.error(e);
                    return;
                }

                if (!response?.heading) return;

                const clientHeading = this.transformLocationHeading(response, clientShift.shiftDetails);
                if (!clientHeading) return;

                if (index === this._activeShiftIndex) {
                    // Active shift: route through the HeadingsStore for validation + shine highlight.
                    headingsStore.addHeading(clientHeading);
                }

                // Always insert into the cached window shift so offline navigation stays correct.
                if (!clientShift.headings.some((row) => row.location?.id === clientHeading.location?.id)) {
                    clientShift.headings.push(clientHeading);
                }
            };

            // Pull the currently viewed shift first so it updates as soon as possible, then the
            // remaining plan shifts in parallel.
            const activeIndex = this._activeShiftIndex;
            const activeIsPlanShift = planShiftIndexes.includes(activeIndex);
            const remainingIndexes = planShiftIndexes.filter((index) => index !== activeIndex);

            for (const locationId of toAdd) {
                if (activeIsPlanShift) {
                    await fetchAndInsert(locationId, activeIndex);
                }
                await Promise.all(remainingIndexes.map((index) => fetchAndInsert(locationId, index)));
            }
        },
    },
});
